<script>
  import { Label, Input } from "sveltestrap";
  import {
    deviceTypes,
    afterDeleteArtboard,
    setActive,
  } from "../../editor/component/artboard.js";
  import { firstLevelMenus } from "../../editor/menuSchema.js";
  import {
    callQueue,
    viewSpecs,
    activeSpec,
    deleteViewSpec,
    unlockedSpecs,
    currentSpec,
    menuConfig,
    systemLog,
    directContext,
    currMenu,
    unlockNew,
    updateUnlockedSpecs,
    soloUnlock,
    setSoloUnlock,
    clearSoloUnlock,
    unlockedSpecsMemory,
    setUnlockedSpecsMemory,
  } from "../../editor/storage.js";
  import { genSpecUid, ViewSpec } from "../../editor/viewSpec.js";
  import { alertYesNo } from "../../alert.js";
  import { validateNamePattern } from "../../editor/utility.js";
  import { onDestroy } from "svelte";
  import { clearHighlight } from "../../editor/direct.js";
  import { setExplorationMode } from "../../editor/exploration/exploration.js";
  import { getAugmentation } from "../../editor/exploration/augment.js";
  import ArtboardEditor from "../component/artboardEditor.svelte";

  // variables
  export let inputFocusAction;
  export let inputBlurAction;

  let artboard_name,
    artboard_width,
    artboard_height,
    screen_type,
    device_ppi,
    device_width,
    device_height;
  let artboardSpec = {};

  let subMenu;
  function setSubMenu(name) {
    subMenu = name;
    if (subMenu == "new") {
      artboardSpec = {
        name: "",
        artboard_width: undefined,
        artboard_height: undefined,
        screen_type: "",
        device_width: undefined,
        device_height: undefined,
        ppi: undefined,
      };
    }
  }
  // config
  menuConfig.subscribe((c) => {
    if (c?.subMenu) {
      setSubMenu(c.subMenu);
      setValues();
    }
  });

  function cancelSubMenu() {
    subMenu = undefined;
  }
  function makeNewArtboard(artboard) {
    let is_first = $viewSpecs.length == 0;
    let uid;
    if (is_first) {
      uid = genSpecUid();
      viewSpecs.update((specs) => {
        specs.push(new ViewSpec(uid, artboard, [], { is_first }));
        return specs;
      });
      callQueue.update((queue) => {
        queue
          .add(
            [
              {
                description: "Make a new artboard",
                specifier: { role: "artboard" },
                action: "modify",
                option: artboard,
              },
              {
                description: "Set width & height",
                specifier: { role: "view" },
                action: "modify",
                option: {
                  width: artboard.artboard_width,
                  height: artboard.artboard_height,
                },
              },
            ],
            [uid]
          )
          .apply();
        return queue;
      });
      $systemLog.log({ message: "Created a new artboard", artboard_uid: uid });
      unlockedSpecs.update((uids) => {
        uids.push(uid);
        return uids;
      });
      $systemLog.log({ message: "Unlock a spec", artboard_uid: uid });
      activeSpec.set(uid);
      $systemLog.log({
        message: "Set as the active spec",
        artboard_uid: uid,
      });
    } else {
      $viewSpecs[0].duplicate(artboard).then((newUID) => {
        uid = newUID;
        setActive(newUID);
        $systemLog.log({
          message: "Duplicate an artboard",
          artboard_uid: $viewSpecs[0].uid,
          duplicated_uid: uid,
        });
        if ($unlockNew) {
          unlockedSpecs.update((uids) => {
            uids.push(uid);
            return uids;
          });
          $systemLog.log({ message: "Unlock a spec", artboard_uid: uid });
          activeSpec.set(uid);
          $systemLog.log({
            message: "Set as the active spec",
            artboard_uid: uid,
          });
          if ($viewSpecs.length == 1) {
            runRecommender("initial", uid);
          } else {
            runRecommender("exploration", uid);
          }
        }
      });
    }
    cancelSubMenu();
  }
  function runRecommender(mode, uid) {
    setSolo(uid);
    setExplorationMode(mode, uid);
    $systemLog.log({ message: "Run " + mode, artboard_uid: uid });
  }
  function setSolo(uid) {
    if ($soloUnlock === undefined) {
      // nothing is solo-unlocked
      setSoloUnlock(uid);
      setUnlockedSpecsMemory($unlockedSpecs);
      updateUnlockedSpecs([uid]);
      $systemLog.log({ message: "Set solo", artboard_uid: uid });
    } else if ($soloUnlock !== undefined && $soloUnlock !== uid) {
      // there's existing solo-unlock
      setSoloUnlock(uid);
      updateUnlockedSpecs([uid]);
      $systemLog.log({ message: "Set solo", artboard_uid: uid });
    }
  }

  // deleting
  function deleteArtboard(uid, name) {
    let onYes = new CustomEvent("onYes_" + uid, {
      detail: { outcome: "yes" },
    });
    let onNo = new CustomEvent("onNo_" + uid, {
      detail: { outcome: "no" },
    });
    alertYesNo({
      title: "Delete an artboard",
      body: "Do you really want to delete Artboard " + name + "?",
      onYes: onYes,
      onNo: onNo,
    });
    function yesHandler(e) {
      document.body.removeEventListener("onYes_" + uid, yesHandler);
      document.body.removeEventListener("onNo_" + uid, noHandler);
      unlockedSpecs.update((items) => {
        return items.filter((d) => d !== uid);
      });
      setInactive(uid);
      afterDeleteArtboard(uid);
      deleteViewSpec(uid);
      $systemLog.log({ message: "Delete an artboard", artboard_uid: uid });
    }
    function noHandler(e) {
      document.body.removeEventListener("onYes_" + uid, yesHandler);
      document.body.removeEventListener("onNo_" + uid, noHandler);
    }
    document.body.addEventListener("onYes_" + uid, yesHandler, {
      once: true,
    });
    document.body.addEventListener("onNo_" + uid, noHandler, { once: true });
  }

  // editing
  function editArtboard(uid) {
    if ($unlockedSpecs.includes(uid)) {
      activeSpec.set(uid);
      setValues();
    }
    subMenu = "editArtboard";
  }
  function setValues() {
    artboardSpec.name = $currentSpec.artboard.name;
    artboardSpec.artboard_width = $currentSpec.artboard.artboard_width;
    artboardSpec.artboard_height = $currentSpec.artboard.artboard_height;
    artboardSpec.screen_type = $currentSpec.artboard.screen_type;
    artboardSpec.ppi = $currentSpec.artboard.ppi;
    artboardSpec.device_width = $currentSpec.artboard.device_width;
    artboardSpec.device_height = $currentSpec.artboard.device_height;
  }
  //set Values!
  function findView(uid) {
    document.querySelector("#dupo-view-" + uid).classList.add("dupo-g-found");
  }
  function unfindView() {
    clearHighlight();
  }
  function checkName(event) {
    let name = event.target.value;
    return validateNamePattern(name);
  }
  let invalidArtboardName;

  function updateArtboard(event, role, uid) {
    let rules = [];
    let view_rule;
    if (role === "name" && event.target.value) {
      rules.push({
        category: "manual",
        description: "Update artboard name",
        specifier: { role: "artboard", uid },
        action: "modify",
        option: { name: event.target.value },
      });
    } else if (role === "artboard_width" && parseInt(event.target.value)) {
      rules.push({
        category: "manual",
        description: "Update artboard width",
        specifier: { role: "artboard", uid },
        action: "modify",
        option: { artboard_width: parseInt(event.target.value) },
      });
      view_rule = {
        category: "manual",
        description: "Update artboard width",
        specifier: { role: "view" },
        action: "modify",
        option: { width: parseInt(event.target.value) },
      };
      rules.push(view_rule);
    } else if (role === "artboard_height" && parseInt(event.target.value)) {
      rules.push({
        category: "manual",
        description: "Update artboard height",
        specifier: { role: "artboard", uid },
        action: "modify",
        option: { artboard_height: parseInt(event.target.value) },
      });
      view_rule = {
        category: "manual",
        description: "Update artboard height",
        specifier: { role: "view" },
        action: "modify",
        option: { height: parseInt(event.target.value) },
      };
      rules.push(view_rule);
    } else if (role === "device_width" && parseInt(event.target.value)) {
      rules.push({
        category: "manual",
        description: "Update device width",
        specifier: { role: "artboard", uid },
        action: "modify",
        option: { device_width: parseInt(event.target.value) },
      });
    } else if (role === "device_height" && parseInt(event.target.value)) {
      rules.push({
        category: "manual",
        description: "Update device height",
        specifier: { role: "artboard", uid },
        action: "modify",
        option: { device_height: parseInt(event.target.value) },
      });
    } else if (role === "device_ppi" && parseInt(event.target.value)) {
      rules.push({
        category: "manual",
        description: "Update device ppi",
        specifier: { role: "artboard", uid },
        action: "modify",
        option: { ppi: parseInt(event.target.value) },
      });
    }

    if (rules) {
      callQueue.update((queue) => {
        queue.add(rules, [uid]).apply({
          afterUpdateAll: () => {
            if (
              view_rule &&
              ["artboard_width", "artboard_height"].includes(role)
            ) {
              getAugmentation(
                role,
                view_rule,
                [
                  $currentSpec?.fronto?.layout?.width,
                  $currentSpec?.fronto?.layout?.height,
                ],
                role === "artboard_width"
                  ? [
                      parseInt(event.target.value),
                      $currentSpec?.fronto?.layout?.height,
                    ]
                  : [
                      $currentSpec?.fronto?.layout?.width,
                      parseInt(event.target.value),
                    ],
                $currentSpec,
                [$currentSpec.uid],
                null
              );
            }
          },
        });
        return queue;
      });
    }
  }

  onDestroy(() => {
    menuConfig.set(undefined);
  });
</script>

<section class="second-level-menu">
  <h2>
    {firstLevelMenus["artboard"]?.title || "Artboard"}
  </h2>

  {#if !subMenu}
    <div class="dupo-menu-pane mb-2">
      <div class="dupo-menu-pane-row">
        <p class="text-sm">Add/edit/remove an artboard(s).</p>
      </div>
    </div>

    <div class="dupo-menu-pane mb-2">
      <div class="dupo-menu-pane-row mb-1">
        <h3 class="h-sub">Artboards</h3>
      </div>
      {#each $viewSpecs as spec}
        <div class="dupo-menu-pane-form-row">
          <div
            class="menu-second-level menu-with-buttons"
            on:mouseover={() => {
              findView(spec.uid);
            }}
            on:mouseout={() => {
              unfindView();
            }}
            on:focus={() => {
              findView(spec.uid);
            }}
            on:blur={() => {
              unfindView();
            }}
          >
            <div class="text-truncate" style="user-select:none;">
              <i
                class={`bi bi-${
                  deviceTypes[spec.artboard.screen_type]?.icon || "display"
                } me-1`}
              />
              {spec.artboard.name}
            </div>
            <div class="dupo-pane-list-button">
              <button
                type="button"
                on:click={() => {
                  editArtboard(spec.uid);
                }}
                disabled={!$unlockedSpecs.includes(spec.uid)}
                ><i
                  class="bi bi-pencil-fill"
                  alt="{`Edit artboard ${spec.artboard.name}`}}"
                /></button
              >
              <button
                type="button"
                on:click={() => {
                  deleteArtboard(spec.uid, spec.artboard.name);
                }}
                disabled={!$unlockedSpecs.includes(spec.uid)}
                ><i
                  class="bi bi-trash-fill"
                  alt="{`Remove artboard ${spec.artboard.name}`}}"
                /></button
              >
            </div>
          </div>
        </div>
      {/each}
    </div>
    <div class="pane-divider" />
    <div class="dupo-menu-pane mb-2">
      <div class="dupo-menu-pane-form-row">
        <button
          class="menu-second-level"
          type="button"
          on:click={() => {
            setSubMenu("new");
          }}><i class="bi bi-plus-circle me-1" />New responsive artboard</button
        >
      </div>
    </div>
  {:else if subMenu == "new"}
    <ArtboardEditor
      artboardSpec={{}}
      mode="new"
      {cancelSubMenu}
      {unfindView}
      {makeNewArtboard}
      {inputBlurAction}
      {inputFocusAction}
    />
  {:else if subMenu == "editArtboard"}
    <ArtboardEditor
      {artboardSpec}
      mode="edit"
      {cancelSubMenu}
      {unfindView}
      {updateArtboard}
      {inputBlurAction}
      {inputFocusAction}
    />
  {:else if subMenu == "editArtboard"}
    <div class="dupo-menu-pane mb-2">
      <div class="dupo-menu-pane-row mb-2">
        <h3 class="h-sub">Edit "{$currentSpec.artboard.name}" artboard</h3>
      </div>
    </div>

    {#if !$unlockedSpecs.includes($currentSpec.uid)}
      <div
        class="dupo-menu-pane mb-2 warn-msg pt-2"
        style="margin-top: -0.5rem;"
      >
        <div class="dupo-menu-pane-row">
          <p class="text-sm">Unlock this artboard first!</p>
        </div>
      </div>
    {:else}
      <!-- name, screen type, devie width, device height, artboard width, artboard height -->
      <div class="dupo-menu-pane mb-2">
        <div class="dupo-menu-pane-row">
          <Label for="artboard_name">Name</Label>
        </div>
        <div class="dupo-menu-pane-form-row">
          <Input
            type="text"
            name="artboard_name"
            id="artboard_name"
            class="custom-form-select"
            autocomplete="off"
            bind:value={artboard_name}
            on:focus={inputFocusAction}
            on:blur={inputBlurAction}
            on:change={(event) => {
              invalidArtboardName = !checkName(event);
              if (!invalidArtboardName)
                updateArtboard(event, "name", $activeSpec);
            }}
          />
        </div>
        {#if invalidArtboardName}
          <div class="dupo-menu-pane-row warn-msg">
            <p class="text-sm">Create or select an artboard!</p>
          </div>
        {/if}
      </div>

      <div class="dupo-menu-pane mb-2">
        <div class="dupo-menu-pane-row">
          <Label for="artboard_width">Artboard Width</Label>
        </div>
        <div class="dupo-menu-pane-form-row">
          <Input
            type="number"
            name="artboard_width"
            id="artboard_width"
            class="custom-form-select"
            autocomplete="off"
            bind:value={artboard_width}
            on:focus={inputFocusAction}
            on:blur={inputBlurAction}
            on:change={(event) => {
              updateArtboard(event, "artboard_width", $activeSpec);
            }}
          />
        </div>
      </div>

      <div class="dupo-menu-pane mb-2">
        <div class="dupo-menu-pane-row">
          <Label for="artboard_height">Artboard Height</Label>
        </div>
        <div class="dupo-menu-pane-form-row">
          <Input
            type="number"
            name="artboard_height"
            id="artboard_height"
            class="custom-form-select"
            autocomplete="off"
            bind:value={artboard_height}
            on:focus={inputFocusAction}
            on:blur={inputBlurAction}
            on:change={(event) => {
              updateArtboard(event, "artboard_height", $activeSpec);
            }}
          />
        </div>
      </div>

      <div class="pane-divider" />

      <div class="dupo-menu-pane mb-2">
        <div class="dupo-menu-pane-row">
          <Label for="screen_type">Screen Type</Label>
        </div>
        <div class="dupo-menu-pane-form-row">
          <Input
            type="text"
            name="screen_type"
            id="screen_type"
            class="custom-form-select"
            autocomplete="off"
            bind:value={screen_type}
            readonly
          />
        </div>
      </div>

      <div class="dupo-menu-pane mb-2">
        <div class="dupo-menu-pane-row">
          <p class="text-sm mb-2">
            <i class="bi bi-exclamation-circle-fill me-1"
              >You cannot edit screen type</i
            >
          </p>
        </div>
      </div>

      <div class="dupo-menu-pane mb-2">
        <div class="dupo-menu-pane-row">
          <Label for="device_width">Device Width</Label>
        </div>
        <div class="dupo-menu-pane-form-row">
          <Input
            type="number"
            name="device_width"
            id="device_width"
            class="custom-form-select"
            autocomplete="off"
            bind:value={device_width}
            on:focus={inputFocusAction}
            on:blur={inputBlurAction}
            on:change={(event) => {
              updateArtboard(event, "device_width", $activeSpec);
            }}
          />
        </div>
      </div>

      <div class="dupo-menu-pane mb-3">
        <div class="dupo-menu-pane-row">
          <Label for="device_height">Device Height</Label>
        </div>
        <div class="dupo-menu-pane-form-row">
          <Input
            type="number"
            name="device_height"
            id="device_height"
            class="custom-form-select"
            autocomplete="off"
            bind:value={device_height}
            on:focus={inputFocusAction}
            on:blur={inputBlurAction}
            on:change={(event) => {
              updateArtboard(event, "device_height", $activeSpec);
            }}
          />
        </div>
      </div>

      <div class="dupo-menu-pane mb-2">
        <div class="dupo-menu-pane-row">
          <Label for="ppi">PPI</Label>
        </div>
        <div class="dupo-menu-pane-form-row">
          <Input
            type="number"
            name="ppi"
            id="ppi"
            class="custom-form-select"
            autocomplete="off"
            bind:value={device_ppi}
            on:focus={inputFocusAction}
            on:blur={inputBlurAction}
            on:change={(event) => {
              updateArtboard(event, "device_ppi", $activeSpec);
            }}
          />
        </div>
      </div>
    {/if}
    <div class="pane-divider" />

    <div class="dupo-menu-pane mb-2">
      <div class="dupo-menu-pane-button-row">
        <button
          type="button"
          on:click={() => {
            if ($directContext) currMenu.set("selection");
            else if ($menuConfig) currMenu.set(undefined);
            else {
              cancelSubMenu();
              unfindView();
            }
          }}><i class="bi bi-arrow-left me-1" />Go back</button
        >
      </div>
    </div>
  {/if}
</section>
